import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  Grid,
  Chip,
  Stack,
} from '@mui/material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { matchPlayerGroupConfigApi, matchPlayerApi, matchApi } from '../../services/api';
import { useNavigate, useParams } from 'react-router-dom';
import MatchAutocomplete from '../MatchAutocomplete';
import { Group, Match, MatchPlayer, MatchPlayerGroupConfig } from '../../models';
import SortablePlayer from './SortablePlayer';
import { lightTheme } from '../../theme';
import { useDroppable } from '@dnd-kit/core';

const LINEUP_STATUS_OPTIONS = [
  { value: "PLAYING", label: "Playing", color: lightTheme.colors.palette.green500 },
  { value: "NOT_PLAYING", label: "Not Playing", color: lightTheme.colors.palette.red500 },
  { value: "UNAVAILABLE", label: "Unavailable", color: lightTheme.colors.palette.red500 },
  { value: "LAST_PLAYED", label: "Last Played", color: lightTheme.colors.palette.blue500 },
  { value: "X_FACTOR_SUBSTITUTE", label: "X Factor", color: lightTheme.colors.palette.yellow500 },
  { value: "USED_X_FACTOR_SUBSTITUTE", label: "Used X Factor", color: lightTheme.colors.palette.yellow500 },
];

const DEFAULT_SELECTED_STATUSES = ["LAST_PLAYED", "PLAYING", "X_FACTOR_SUBSTITUTE", "USED_X_FACTOR_SUBSTITUTE"];

const DroppableGroup = ({ group, children }: { group: Group; children: React.ReactNode }) => {
  const { setNodeRef, isOver} = useDroppable({
    id: group.id,
    data: {
      type: 'group',
    },
  });

  return (
    <Paper
      id={group.id}
      ref={setNodeRef}
      sx={{ 
        p: 2, 
        width: '300px',
        backgroundColor: 'rgba(0, 0, 255, 0.05)',
        position: 'relative',
        minHeight: '200px',
        border: '2px solid rgba(0, 0, 255, 0.2)',
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
          border: isOver ? '2px dashed red' : 'none',
          backgroundColor: isOver ? 'rgba(255, 0, 0, 0.1)' : 'transparent',
          zIndex: 1,
        }
      }}
    >
      {children}
    </Paper>
  );
};

const GroupConfiguration = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [match, setMatch] = useState<Match | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [availablePlayers, setAvailablePlayers] = useState<MatchPlayer[]>([]);
  const [name, setName] = useState('');
  const [isSystem, setIsSystem] = useState(false);
  const [totalSelectCount, setTotalSelectCount] = useState(0);
  const [error, setError] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(DEFAULT_SELECTED_STATUSES);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (id) {
      fetchConfig();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);


  const fetchConfig = async () => {
    try {
      const response = await matchPlayerGroupConfigApi.getById(parseInt(id!));
      const config = response.data as MatchPlayerGroupConfig;
      
      // Set basic config data
      setName(config.name);
      setIsSystem(config.is_system);
      
      // Set match data and fetch players
      if (config.match) {
        const matchResponse = await matchApi.getById(config.match);
        setMatch(matchResponse.data);
        await fetchMatchPlayers(config.match);
      }
      
      // Set groups after players are fetched
      setGroups(config.configuration.groups);
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const fetchMatchPlayers = async (matchId: number) => {
    try {
      const response = await matchPlayerApi.getByMatch(matchId);
      setAvailablePlayers(response.data.results);
    } catch (error) {
      console.error('Error fetching match players:', error);
    }
  };

  const handleMatchChange = async (selectedMatch: Match | null) => {
    if (selectedMatch) {
      setMatch(selectedMatch);
      setGroups([]);
      await fetchMatchPlayers(selectedMatch.id);
    } else {
      setMatch(null);
      setGroups([]);
      setAvailablePlayers([]);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      return;
    }

    const activeId = active.id.toString();
    const overId = over.id.toString();

    if (activeId === overId) {
      return;
    }

    // Moving between groups
    if (active.data.current?.type === 'group' && over.data.current?.type === 'group') {
      setGroups((items) => {
        const oldIndex = items.findIndex((item) => item.id === activeId);
        const newIndex = items.findIndex((item) => item.id === overId);
        return arrayMove(items, oldIndex, newIndex);
      });
    }

    // Moving from available players to a group
    if (active.data.current?.type === 'player' && over.data.current?.type === 'group') {
      const playerId = parseInt(activeId);
      const targetGroupId = overId;
      const matchPlayer = availablePlayers.find(p => p.id === playerId);

      if (!matchPlayer) return;

      setGroups((prevGroups) => {
        return prevGroups.map((group) => {
          if (group.id === targetGroupId) {
            // Only add if not already in the group
            if (!group.players.includes(matchPlayer.player.id)) {
              return {
                ...group,
                players: [...group.players, matchPlayer.player.id],
              };
            }
          }
          return group;
        });
      });
    }

    // Moving between groups
    if (active.data.current?.type === 'player' && over.data.current?.type === 'player') {
      const playerId = parseInt(activeId);
      const sourceGroupId = active.data.current.groupId;
      const targetGroupId = over.data.current.groupId;
      const matchPlayer = availablePlayers.find(p => p.id === playerId);

      if (!matchPlayer) return;

      if (sourceGroupId === targetGroupId) {
        return;
      }

      setGroups((prevGroups) => {
        return prevGroups.map((group) => {
          if (group.id === sourceGroupId) {
            return {
              ...group,
              players: group.players.filter((id) => id !== matchPlayer.player.id),
            };
          }
          if (group.id === targetGroupId) {
            return {
              ...group,
              players: [...group.players, matchPlayer.player.id],
            };
          }
          return group;
        });
      });
    }
  };

  const addGroup = () => {
    if (groups.length >= 11) return;
    
    const newGroup: Group = {
      id: Date.now().toString(),
      name: `Group ${groups.length + 1}`,
      players: [],
      select_count: 1,
      is_captain_group: false,
      is_vice_captain_group: false,
    };
    
    setGroups([...groups, newGroup]);
  };

  const removeGroup = (index: number) => {
    setGroups(groups.filter((_, i) => i !== index));
  };

  const updateGroup = (index: number, field: keyof Group, value: any) => {
    setGroups(prevGroups => {
      const newGroups = [...prevGroups];
      newGroups[index] = { ...newGroups[index], [field]: value };
      return newGroups;
    });
  };

  useEffect(() => {
    const total = groups.reduce((sum, group) => sum + group.select_count, 0);
    setTotalSelectCount(total);
  }, [groups]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    if (!match) {
      setError('Please select a match');
      return;
    }

    if (!name) {
      setError('Please enter a name');
      return;
    }

    if (groups.length === 0) {
      setError('Please create at least one group');
      return;
    }

    if (totalSelectCount !== 11) {
      setError('Total select count must be 11');
      return;
    }

    const config = {
      name,
      match: match.id,
      is_system: isSystem,
      configuration: {
        groups,
      },
    };

    try {
      if (id) {
        await matchPlayerGroupConfigApi.update(parseInt(id), config);
      } else {
        await matchPlayerGroupConfigApi.create(config);
      }
      navigate('/match-player-group-configs');
    } catch (error) {
      console.error('Error saving config:', error);
      setError('Error saving configuration');
    }
  };

  const handleStatusToggle = (status: string) => {
    setSelectedStatuses(prev => {
      if (prev.includes(status)) {
        return prev.filter(s => s !== status);
      }
      return [...prev, status];
    });
  };

  const filteredPlayers = availablePlayers
    .filter(player => selectedStatuses.includes(player.lineup_status || ""))
    .sort((a, b) => {
      // First sort by team
      if (a.team_name !== b.team_name) {
        return a.team_name.localeCompare(b.team_name);
      }
      // Then sort by batting position
      const posA = a.prev_bat_pos || 999;
      const posB = b.prev_bat_pos || 999;
      return posA - posB;
    });

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        {id ? 'Edit' : 'Create'} Match Player Group Configuration
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        />

        <Box sx={{ mb: 2 }}>
          <MatchAutocomplete
            value={match}
            onChange={handleMatchChange}
            error={!!(error && !match)}
            helperText={error && !match ? 'Please select a match' : ''}
          />
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={isSystem}
              onChange={(e) => setIsSystem(e.target.checked)}
              color="primary"
            />
          }
          label="System Configuration"
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Group Configuration
        </Typography>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <Box display="flex" gap={2}>
            {/* Available Players */}
            <Paper sx={{ p: 2, width: '300px' }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Available Players
              </Typography>
              
              <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
                {LINEUP_STATUS_OPTIONS.map((status) => (
                  <Chip
                    key={status.value}
                    label={status.label}
                    onClick={() => handleStatusToggle(status.value)}
                    sx={{
                      backgroundColor: selectedStatuses.includes(status.value) ? status.color : 'transparent',
                      border: `1px solid ${status.color}`,
                      color: selectedStatuses.includes(status.value) ? 'white' : status.color,
                      '&:hover': {
                        backgroundColor: selectedStatuses.includes(status.value) ? status.color : `${status.color}20`,
                      },
                    }}
                  />
                ))}
              </Stack>

              <SortableContext
                items={filteredPlayers.map(p => p.id.toString())}
                strategy={rectSortingStrategy}
              >
                <Grid container spacing={1}>
                  {filteredPlayers
                    .filter(p => !groups.some(g => g.players.includes(p.player.id)))
                    .map((player) => (
                      <Grid item xs={12} key={player.id}>
                        <SortablePlayer
                          matchPlayer={player}
                          groupId="available"
                          type="player"
                        />
                      </Grid>
                    ))}
                </Grid>
              </SortableContext>
            </Paper>

            {/* Groups */}
            <Box display="flex" flexWrap="wrap" gap={2}>
              {groups.map((group, index) => (
                <DroppableGroup key={group.id} group={group}>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <TextField
                      label="Group Name"
                      value={group.name}
                      onChange={(e) => updateGroup(index, 'name', e.target.value)}
                      size="small"
                    />
                    <Button
                      onClick={() => removeGroup(index)}
                      color="error"
                      size="small"
                    >
                      Remove
                    </Button>
                  </Box>

                  <TextField
                    label="Select Count"
                    type="number"
                    value={group.select_count}
                    onChange={(e) => {
                      const value = Math.min(11, Math.max(1, parseInt(e.target.value) || 1));
                      updateGroup(index, 'select_count', value);
                    }}
                    size="small"
                    fullWidth
                    sx={{ mb: 1 }}
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={group.is_captain_group}
                        onChange={(e) => updateGroup(index, 'is_captain_group', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Captain Group"
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={group.is_vice_captain_group}
                        onChange={(e) => updateGroup(index, 'is_vice_captain_group', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Vice Captain Group"
                  />

                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Players
                    </Typography>
                    <SortableContext
                      items={group.players.map(p => p.toString())}
                      strategy={rectSortingStrategy}
                    >
                      <Grid container spacing={1}>
                        {group.players.map((playerId) => {
                          const player = availablePlayers.find(p => p.player.id === playerId);
                          return player ? (
                            <Grid item xs={12} key={playerId}>
                              <SortablePlayer
                                matchPlayer={player}
                                groupId={group.id}
                                type="player"
                              />
                            </Grid>
                          ) : null;
                        })}
                      </Grid>
                    </SortableContext>
                  </Box>
                </DroppableGroup>
              ))}
            </Box>
          </Box>
        </DndContext>

        <Box mt={2}>
          <Button
            variant="contained"
            onClick={addGroup}
            disabled={groups.length >= 11}
          >
            Add Group
          </Button>
        </Box>

        <Typography
          variant="body2"
          color={totalSelectCount === 11 ? 'success.main' : 'error.main'}
          sx={{ mt: 1 }}
        >
          Total Select Count: {totalSelectCount}/11
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
        >
          {id ? 'Update' : 'Create'}
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate('/match-player-group-configs')}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default GroupConfiguration; 